import {
  type FocusEvent,
  type PointerEvent,
  type ReactNode,
  useRef,
  useState,
} from "react";
import { Link } from "react-router";
import {
  ArrowUpRight,
  CircleCheck,
  OctagonAlert,
  TriangleAlert,
} from "lucide-react";
import type { Approval, OkrObjective } from "../../api/hub/collaboration";
import type { CalendarItem, Project } from "../../api/hub/types";
import { taskDate } from "../tasks/time";
import { DataSource, DataState, MoreButton } from "./Frame";
import { listItems, message, useDataList } from "./data";
import { readableOkrPeriod } from "./okrLabels";
import {
  AI_STATES,
  aiState,
  daysBetween,
  HEALTH,
  type Listed,
  STAGES,
  useAiWeek,
  useClock,
  useMyProjects,
  useTaskPulse,
  useWeekAgenda,
  weekdayLabel,
} from "./dashboardData";

// 仪表盘只放概览：每个区块一个结论、一张小图；明细折叠，完整清单在各业务页面。
// 数据时间统一显示在页头，区块里不再逐个写「数据更新于」。
const clamp = (value: number) => Math.max(0, Math.min(100, value));
const percent = (part: number, whole: number) =>
  whole ? Math.round(part / whole * 100) + "%" : "0%";
const shortDate = (day: string) =>
  Number(day.slice(5, 7)) + "/" + Number(day.slice(8, 10));

type Tip = { x: number; y: number; label: string; value: string };
/** One tooltip per chart; the same readout appears on hover and keyboard focus. */
function useTip() {
  const box = useRef<HTMLDivElement>(null);
  const [tip, setTip] = useState<Tip | null>(null);
  function bind(label: string, value: string) {
    const show = (target: Element) => {
      const outer = box.current?.getBoundingClientRect(),
        mark = target.getBoundingClientRect();
      if (outer) {
        setTip({
          x: mark.left - outer.left + mark.width / 2,
          y: mark.top - outer.top,
          label,
          value,
        });
      }
    };
    return {
      tabIndex: 0,
      "aria-label": label + "：" + value,
      onPointerEnter: (e: PointerEvent<Element>) => show(e.currentTarget),
      onFocus: (e: FocusEvent<Element>) => show(e.currentTarget),
      onPointerLeave: () => setTip(null),
      onBlur: () => setTip(null),
    };
  }
  const view = tip && (
    <div
      className="dash-tip"
      role="tooltip"
      style={{ left: tip.x, top: tip.y }}
    >
      <strong>{tip.value}</strong>
      <span>{tip.label}</span>
    </div>
  );
  return { box, bind, view };
}

export function DashCard(
  { id, title, icon, link, className = "", children }: {
    id: string;
    title: string;
    icon: ReactNode;
    link?: { to: string; label: string };
    className?: string;
    children: ReactNode;
  },
) {
  return (
    <section className={"dash-card " + className} aria-labelledby={id}>
      <header className="dash-card-head">
        <span className="dash-card-icon" aria-hidden="true">{icon}</span>
        <h2 id={id}>{title}</h2>
        {link && (
          <Link className="dash-card-link" to={link.to}>
            {link.label}
            <ArrowUpRight size={13} aria-hidden="true" />
          </Link>
        )}
      </header>
      {children}
    </section>
  );
}

type Part = { key: string; label: string; value: number; color: string };
function StackBar({ parts, label }: { parts: Part[]; label: string }) {
  const tip = useTip(), total = parts.reduce((sum, p) => sum + p.value, 0);
  return (
    <div className="dash-chart" ref={tip.box}>
      <div className="dash-stack" role="group" aria-label={label}>
        {total === 0
          ? <span className="dash-stack-empty" />
          : parts.filter((p) => p.value > 0).map((p) => (
            <span
              key={p.key}
              className="dash-stack-seg"
              style={{ flexGrow: p.value, background: p.color }}
              {...tip.bind(p.label, p.value + " · " + percent(p.value, total))}
            />
          ))}
      </div>
      {tip.view}
    </div>
  );
}
/** Inline legend: swatch + label + count, one line. */
function Legend({ parts }: { parts: Part[] }) {
  return (
    <ul className="dash-legend">
      {parts.map((p) => (
        <li key={p.key}>
          <span className="dash-swatch" style={{ background: p.color }} />
          {p.label} <strong>{p.value}</strong>
        </li>
      ))}
    </ul>
  );
}
function Meter({ value, label }: { value: number; label: string }) {
  const known = Number.isFinite(value);
  return (
    <span
      className="dash-meter"
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={known ? Math.round(value) : undefined}
    >
      <span style={{ width: (known ? clamp(value) : 0) + "%" }} />
    </span>
  );
}
function Ring({ value, label }: { value: number; label: string }) {
  const size = 52, stroke = 5, r = (size - stroke) / 2, c = 2 * Math.PI * r;
  const known = Number.isFinite(value), v = known ? clamp(value) : 0;
  return (
    <svg
      className="dash-ring"
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      role="img"
      aria-label={label +
        (known ? " 进度 " + Math.round(v) + "%" : " 进度未记录")}
    >
      <circle
        className="dash-ring-track"
        cx={size / 2}
        cy={size / 2}
        r={r}
        strokeWidth={stroke}
      />
      {v > 0 && (
        <circle
          className="dash-ring-fill"
          cx={size / 2}
          cy={size / 2}
          r={r}
          strokeWidth={stroke}
          strokeDasharray={`${c * v / 100} ${c}`}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      )}
      <text x="50%" y="50%" dominantBaseline="central" textAnchor="middle">
        {known ? Math.round(v) + "%" : "—"}
      </text>
    </svg>
  );
}

/** 指标只显示名称和数字；解释放在悬停提示里，点进去是对应业务页面。 */
function Kpi(
  { to, label, value, detail, tone, meter, error }: {
    to: string;
    label: string;
    value: string | null;
    detail: string;
    tone?: "danger" | "warn";
    meter?: number;
    error?: string;
  },
) {
  return (
    <Link
      className={"dash-kpi" + (tone ? " is-" + tone : "")}
      to={to}
      title={detail}
    >
      <span className="dash-kpi-label">{label}</span>
      <strong className="dash-kpi-value">{value ?? "—"}</strong>
      {meter !== undefined && <Meter value={meter} label={label} />}
      {error && <small className="home-error" role="alert">{error}</small>}
    </Link>
  );
}
const total = (query: { data?: Listed<unknown>; isError: boolean }) =>
  query.data?.total ?? null;
const shown = (value: number | null) => value === null ? null : String(value);
export function KpiStrip() {
  const pulse = useTaskPulse(),
    { projects, query: projectQuery } = useMyProjects();
  const approvals = useDataList<Approval>("approvals_pending", { limit: 5 }),
    okr = useDataList<OkrObjective>("okr_mine", { limit: 5 });
  const approvalCount = approvals.data
    ? approvals.data.pages[0]?.pagination?.total ??
      (approvals.hasNextPage ? null : listItems(approvals.data.pages).length)
    : null;
  const objectives = listItems(okr.data?.pages).filter((o) =>
    Number.isFinite(o.progress)
  );
  const okrAverage = objectives.length
    ? objectives.reduce((sum, o) => sum + clamp(o.progress), 0) /
      objectives.length
    : null;
  const offTrack = projects.filter((p) => p.health === "off_track").length,
    atRisk = projects.filter((p) => p.health === "at_risk").length;
  const readError = (query: { isError: boolean; error: unknown }) =>
    query.isError ? message(query.error) : undefined;
  const overdue = total(pulse.overdue),
    unread = (query: { isError: boolean; data?: unknown }) =>
      query.isError && !query.data ? "暂未读到，稍后刷新" : null;
  return (
    <section className="dash-kpis" aria-label="关键指标">
      <Kpi
        to="/tasks"
        label="进行中"
        error={readError(pulse.stage.in_progress)}
        value={shown(total(pulse.stage.in_progress))}
        detail={unread(pulse.stage.in_progress) ??
          `待办 ${total(pulse.stage.todo) ?? "—"} · 待验收 ${
            total(pulse.stage.in_review) ?? "—"
          }`}
      />
      <Kpi
        to="/tasks"
        label="已逾期"
        error={readError(pulse.overdue)}
        value={shown(overdue)}
        tone={overdue ? "danger" : undefined}
        detail={unread(pulse.overdue) ??
          (overdue ? "未完成且已过截止日" : "没有逾期事项")}
      />
      <Kpi
        to="/calendar"
        label="本周到期"
        error={readError(pulse.dueWeek)}
        value={shown(pulse.dueWeekCount)}
        detail={unread(pulse.dueWeek) ??
          `今天到期 ${pulse.dueTodayCount ?? "—"} 件`}
      />
      <Kpi
        to="/approvals"
        label="待我审批"
        error={readError(approvals)}
        value={shown(approvalCount)}
        tone={approvalCount ? "warn" : undefined}
        detail={unread(approvals) ??
          (approvalCount ? "核对依据后决定" : "没有待处理的审批")}
      />
      <Kpi
        to="/projects"
        label="风险项目"
        error={readError(projectQuery)}
        value={projectQuery.data
          ? String(offTrack + atRisk) + (projectQuery.data.complete ? "" : "+")
          : null}
        tone={offTrack ? "danger" : atRisk ? "warn" : undefined}
        detail={unread(projectQuery) ??
          (offTrack + atRisk
            ? `偏离计划 ${offTrack} · 有风险 ${atRisk}`
            : `已读 ${projects.length} 个关注项目`)}
      />
      <Kpi
        to="/okr"
        label="目标进度"
        error={readError(okr)}
        value={okrAverage === null || okr.hasNextPage
          ? null
          : Math.round(okrAverage) + "%"}
        meter={okr.hasNextPage ? undefined : okrAverage ?? undefined}
        detail={unread(okr) ??
          (objectives.length
            ? `${objectives.length} 个目标的平均进度`
            : "当前周期还没有目标")}
      />
    </section>
  );
}

export function TaskPipeline() {
  const pulse = useTaskPulse();
  const queries = [...Object.values(pulse.stage), pulse.done],
    failed = queries.find((q) => q.isError && !q.data);
  const state = {
    isPending: queries.some((q) => q.isPending),
    isError: !!failed,
    error: failed?.error,
    refetch: () => queries.forEach((q) => void q.refetch()),
    data: failed ? undefined : true,
  };
  const parts = STAGES.map((stage, i) => ({
    key: stage.key,
    label: stage.label,
    value: pulse.stage[stage.key].data?.total ?? 0,
    color: `var(--dash-stage-${i + 1})`,
  }));
  const open = parts.reduce((sum, p) => sum + p.value, 0),
    done = pulse.done.data?.total ?? 0;
  return (
    <DataState query={state}>
      <div className="dash-figure">
        <strong>{open}</strong>
        <span>件未完成</span>
      </div>
      <StackBar parts={parts} label="未完成任务按阶段分布" />
      <Legend parts={parts} />
      <p className="dash-faint dash-completion">
        完成率 <strong>{percent(done, open + done)}</strong> · 已完成 {done} /
        {" "}
        {open + done}
      </p>
    </DataState>
  );
}

const HEALTH_ICON = {
  off_track: <OctagonAlert size={13} />,
  at_risk: <TriangleAlert size={13} />,
  on_track: <CircleCheck size={13} />,
};
function HealthChip({ health }: { health: string }) {
  const known = HEALTH.find((h) => h.key === health);
  if (!known) return <span className="dash-health-chip">状态待核对</span>;
  return (
    <span className={"dash-health-chip is-" + known.key}>
      <span aria-hidden="true">{HEALTH_ICON[known.key]}</span>
      {known.label}
    </span>
  );
}
function ProjectRow({ project, today }: { project: Project; today: string }) {
  const progress = Number.isFinite(project.progress)
    ? Math.round(clamp(project.progress))
    : null;
  const start = project.startDate, end = project.targetDate;
  const span = start && end ? daysBetween(start, end) : 0;
  const elapsed = start && span > 0
    ? clamp(daysBetween(start, today) / span * 100)
    : null;
  const left = end ? daysBetween(today, end) : null;
  const lag = elapsed !== null && progress !== null
    ? Math.round(elapsed - progress)
    : 0;
  const milestones = start && span > 0
    ? (project.milestones ?? []).filter((m) =>
      m.date >= start && m.date <= (end ?? m.date)
    )
    : [];
  return (
    <li className="dash-project">
      <div className="dash-project-name">
        <Link to={"/projects?project=" + encodeURIComponent(project.id)}>
          {project.title}
        </Link>
        <p>
          <HealthChip health={project.health} />
          {lag >= 15 && (
            <span className="dash-lag" title="完成进度比已过去的时间少这么多">
              · 进度落后时间 {lag}%
            </span>
          )}
        </p>
      </div>
      <div
        className="dash-track"
        role="img"
        aria-label={`${project.title}：完成 ${progress ?? "未记录"}%` +
          (elapsed !== null ? `，时间过去 ${Math.round(elapsed)}%` : "")}
        title={`完成 ${progress ?? "—"}%` +
          (elapsed !== null ? ` · 时间过去 ${Math.round(elapsed)}%` : "") +
          (milestones.length ? ` · 里程碑 ${milestones.length} 个` : "")}
      >
        <span
          className="dash-track-fill"
          style={{ width: (progress ?? 0) + "%" }}
        />
        {milestones.map((m) => (
          <span
            key={m.id}
            className={"dash-track-milestone" + (m.completed ? " is-done" : "")}
            style={{
              left: clamp(daysBetween(start!, m.date) / span * 100) + "%",
            }}
            title={m.title + " · " + m.date + (m.completed ? " · 已完成" : "")}
          />
        ))}
        {elapsed !== null && (
          <span className="dash-track-today" style={{ left: elapsed + "%" }} />
        )}
      </div>
      <div className="dash-project-meta">
        <strong>{progress ?? "—"}%</strong>
        <span>
          {left === null
            ? "未设交付日"
            : left >= 0
            ? `剩 ${left} 天`
            : `超期 ${-left} 天`}
        </span>
      </div>
    </li>
  );
}
export function ProjectRisk() {
  const { today } = useClock(), { query, projects, me } = useMyProjects();
  const [showAll, setShowAll] = useState(false);
  const risky = projects.filter((p) => p.health !== "on_track"),
    steady = projects.length - risky.length;
  // 默认只列有风险的；正常推进的收起，最多列 6 个，其余去项目页。
  const list = showAll ? projects : risky,
    visible = list.slice(0, 6),
    hidden = list.length - visible.length;
  if (me.isError) return <DataState query={me}>{null}</DataState>;
  return (
    <DataState
      query={query}
      isEmpty={!projects.length}
      empty={
        <>
          当前没有你负责或参与的进行中项目。<Link to="/projects">
            查看全部项目 ↗
          </Link>
        </>
      }
    >
      {!risky.length && !showAll && (
        <p className="dash-calm">
          <CircleCheck size={16} aria-hidden="true" />关注项目没有已报告的风险。
        </p>
      )}
      {visible.length > 0 && (
        <ol className="dash-timeline">
          {visible.map((project) => (
            <ProjectRow key={project.id} project={project} today={today} />
          ))}
        </ol>
      )}
      {(steady > 0 || hidden > 0) && (
        <div className="dash-card-foot">
          {steady > 0 && (
            <button
              type="button"
              className="dash-text-button"
              aria-expanded={showAll}
              onClick={() => setShowAll((v) => !v)}
            >
              {showAll ? "只看有风险的" : `另有 ${steady} 个正常推进的项目`}
            </button>
          )}
          {hidden > 0 && (
            <Link className="dash-more-link" to="/projects">
              还有 {hidden} 个 ↗
            </Link>
          )}
        </div>
      )}
      <DataSource result={query.data?.result} href="/projects" />
    </DataState>
  );
}

const KINDS = [
  { key: "task", label: "任务截止" },
  { key: "delivery", label: "交付" },
  { key: "okr", label: "目标提醒" },
  { key: "milestone", label: "里程碑" },
  { key: "approval", label: "审批" },
] as const;
const kindColor = (kind: string) =>
  `var(--dash-kind-${KINDS.some((k) => k.key === kind) ? kind : "task"})`;
const kindLink = (item: CalendarItem) =>
  item.kind === "task"
    ? "/tasks?task=" + encodeURIComponent(item.sourceId)
    : "/calendar?date=" + encodeURIComponent(item.date);
export function WeekRhythm() {
  const { today, me } = useClock(), { query, days } = useWeekAgenda();
  const [picked, setPicked] = useState<string | null>(null);
  const items = query.data?.items ?? [];
  const byDay = days.map((day) => items.filter((item) => item.date === day));
  const max = Math.max(1, ...byDay.map((list) => list.length));
  // 默认看今天；今天没有安排时跳到本周下一个有安排的日子。
  const selected = picked ??
    (days.find((day, i) => day >= today && byDay[i].length) ??
      (days.includes(today) ? today : days[0]));
  const agenda = byDay[days.indexOf(selected)] ?? [];
  if (me.isError) return <DataState query={me}>{null}</DataState>;
  return (
    <DataState query={query}>
      <div className="dash-week" role="group" aria-label="选择查看哪一天">
        {days.map((day, i) => {
          const n = byDay[i].length;
          return (
            <button
              key={day}
              type="button"
              aria-pressed={day === selected}
              title={`${day} · ${n ? n + " 项安排" : "没有安排"}`}
              className={"dash-day" + (day === today ? " is-today" : "") +
                (day < today ? " is-past" : "")}
              onClick={() => setPicked(day)}
            >
              <span className="dash-day-name">
                {day === today ? "今天" : weekdayLabel(day)}
              </span>
              <span className="dash-day-bar" aria-hidden="true">
                <span style={{ height: n / max * 100 + "%" }} />
              </span>
              <span className="dash-day-count">{n ? n + " 项" : "—"}</span>
            </button>
          );
        })}
      </div>
      <div className="dash-agenda" aria-live="polite">
        <p className="dash-agenda-title">
          {weekdayLabel(selected)} {shortDate(selected)}
        </p>
        {agenda.length
          ? (
            <ul>
              {agenda.slice(0, 4).map((item) => (
                <li key={item.id}>
                  <span
                    className="dash-dot"
                    style={{ background: kindColor(item.kind) }}
                  />
                  <Link to={kindLink(item)}>{item.title}</Link>
                  <span className="dash-agenda-kind">
                    {KINDS.find((k) => k.key === item.kind)?.label ?? item.kind}
                  </span>
                </li>
              ))}
            </ul>
          )
          : (
            <p className="dash-faint">
              {items.length ? "这一天没有安排。" : "这周没有记录在中台的安排。"}
            </p>
          )}
        {agenda.length > 4 && (
          <Link className="dash-more-link" to={"/calendar?date=" + selected}>
            这天还有 {agenda.length - 4} 项 ↗
          </Link>
        )}
      </div>
      <DataSource result={query.data?.result} href="/calendar" />
    </DataState>
  );
}

const CONFIDENCE: Record<string, string> = {
  "-1": "未评估",
  "0": "正常",
  "1": "有风险",
  "2": "已延期",
  unassessed: "未评估",
  on_track: "正常",
  off_track: "已延期",
  high: "高",
  normal: "正常",
  medium: "中",
  low: "低",
  at_risk: "有风险",
};
export function OkrBoard() {
  // Period IDs are opaque; the canonical source already returns active objectives.
  const query = useDataList<OkrObjective>("okr_mine", { limit: 5 }),
    items = listItems(query.data?.pages);
  return (
    <DataState
      query={query}
      isEmpty={!items.length}
      empty={
        <>
          当前周期还没有你负责的目标。<Link to="/okr">查看与设置 OKR ↗</Link>
        </>
      }
    >
      {items.map((objective) => (
        <article className="dash-objective" key={objective.id}>
          <Ring value={objective.progress} label={objective.title} />
          <div className="dash-objective-body">
            <h3>
              <Link to={"/okr?objective=" + encodeURIComponent(objective.id)}>
                {objective.title}
              </Link>
            </h3>
            <p className="dash-objective-meta">
              {readableOkrPeriod(objective.period) && (
                <span>{readableOkrPeriod(objective.period)}</span>
              )}
              {objective.confidence && (
                <span>
                  信心{Object.hasOwn(CONFIDENCE, objective.confidence)
                    ? CONFIDENCE[objective.confidence]
                    : "待核对"}
                </span>
              )}
            </p>
            {!!objective.keyResults?.length && (
              <details className="dash-fold">
                <summary>{objective.keyResults.length} 个关键结果</summary>
                <ul className="dash-krs">
                  {objective.keyResults.map((kr) => (
                    <li key={kr.id}>
                      <span className="dash-kr-title">{kr.title}</span>
                      <Meter value={kr.progress} label={kr.title} />
                      <span className="dash-kr-value">
                        {Number.isFinite(kr.progress)
                          ? Math.round(kr.progress) + "%"
                          : "—"}
                      </span>
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        </article>
      ))}
      <MoreButton query={query} />
      <DataSource result={query.data?.pages.at(-1)} href="/okr" />
    </DataState>
  );
}

export function AiWeek() {
  const { query, days, timezone } = useAiWeek(), { today, me } = useClock();
  const tip = useTip(), items = query.data?.items ?? [];
  const columns = days.map((day) => {
    const list = items.filter((a) => taskDate(a.createdAt, timezone) === day);
    return {
      day,
      total: list.length,
      counts: AI_STATES.map((s) =>
        list.filter((a) => aiState(a) === s.key).length
      ),
    };
  });
  const max = Math.max(1, ...columns.map((c) => c.total));
  const parts = AI_STATES.map((s, i) => ({
    key: s.key,
    label: s.label,
    value: columns.reduce((sum, c) => sum + c.counts[i], 0),
    color: `var(--dash-ai-${s.key})`,
  }));
  const counted = parts.reduce((sum, p) => sum + p.value, 0),
    review = parts.find((p) => p.key === "review")?.value ?? 0;
  if (me.isError) return <DataState query={me}>{null}</DataState>;
  return (
    <DataState
      query={query}
      isEmpty={!counted}
      empty="近 7 天没有你能看到的 AI 动作。"
    >
      <div className="dash-figure">
        <strong>{counted}</strong>
        <span>
          次 AI 动作 · 近 7 天{review ? `，${review} 次等人核对` : ""}
        </span>
      </div>
      <div className="dash-columns dash-chart" ref={tip.box}>
        {columns.map((c) => (
          <div
            key={c.day}
            className={"dash-column" + (c.day === today ? " is-today" : "")}
          >
            <div
              className="dash-column-plot"
              {...tip.bind(
                weekdayLabel(c.day) + " " + shortDate(c.day),
                c.total
                  ? c.total + " 次 · " +
                    AI_STATES.map((s, i) => [s.label, c.counts[i]] as const)
                      .filter(([, n]) => n).map(([l, n]) => l + " " + n).join(
                        "，",
                      )
                  : "没有 AI 动作",
              )}
            >
              <span
                className="dash-column-stack"
                style={{ height: c.total / max * 100 + "%" }}
              >
                {AI_STATES.map((s, i) =>
                  c.counts[i]
                    ? (
                      <span
                        key={s.key}
                        style={{
                          flexGrow: c.counts[i],
                          background: `var(--dash-ai-${s.key})`,
                        }}
                      />
                    )
                    : null
                )}
              </span>
            </div>
            <span className="dash-column-label">
              {c.day === today ? "今天" : weekdayLabel(c.day).slice(1)}
            </span>
          </div>
        ))}
        {tip.view}
      </div>
      <Legend parts={parts.filter((p) => p.value)} />
      {!query.data?.complete && (
        <p className="dash-faint">只统计了最近 100 条，完整记录在项目管家。</p>
      )}
    </DataState>
  );
}
